import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

type DirtyNavigationValue = {
  isDirty: boolean;
  setDirty: (dirty: boolean) => void;
  confirmNavigation: () => boolean;
};

const DirtyNavigationContext = createContext<DirtyNavigationValue | null>(null);
const NOOP_VALUE: DirtyNavigationValue = { isDirty: false, setDirty: () => undefined, confirmNavigation: () => true };

export function DirtyNavigationGuardProvider({ children }: { children: ReactNode }) {
  const [isDirty, setDirty] = useState(false);
  useEffect(() => {
    if (!isDirty) return undefined;
    const handleBeforeUnload = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);
  const confirmNavigation = useCallback(() => !isDirty || window.confirm('Há alterações não salvas. Deseja sair mesmo assim?'), [isDirty]);
  const value = useMemo(() => ({ isDirty, setDirty, confirmNavigation }), [confirmNavigation, isDirty]);
  return <DirtyNavigationContext.Provider value={value}>{children}</DirtyNavigationContext.Provider>;
}

export function useDirtyNavigationGuard(): DirtyNavigationValue {
  return useContext(DirtyNavigationContext) ?? NOOP_VALUE;
}

export function useDirtyForm(value: unknown, cleanValue: unknown): void {
  const { setDirty } = useDirtyNavigationGuard();
  const serializedValue = JSON.stringify(value);
  const serializedCleanValue = JSON.stringify(cleanValue);
  useEffect(() => {
    setDirty(serializedValue !== serializedCleanValue);
    return () => setDirty(false);
  }, [serializedCleanValue, serializedValue, setDirty]);
}
